import { shorthands } from "@tamagui/shorthands";
import { createTamagui } from "tamagui";

import { themes, tokens } from "@tamagui/themes";

// Updated to use Poppins instead of Inter
// Note: Tamagui doesn't directly use font files; we just define the font family
const fonts = {
  heading: {
    family: "Poppins",
    size: {
      1: 13,
      2: 14,
      3: 16,
      4: 18,
      5: 22,
      6: 26,
      7: 32,
      8: 40,
      9: 48,
      10: 56,
      11: 64,
      12: 72,
    },
    weight: {
      4: "400",
      5: "500",
      6: "600",
      7: "700",
      8: "800",
      9: "900",
    },
    letterSpacing: {
      4: 0,
      5: -0.25,
      6: -0.5,
      7: -0.75,
      8: -1,
      9: -1.5,
      10: -2,
      11: -2.5,
      12: -3,
    },
    face: {
      100: { normal: "Poppins-Thin" },
      200: { normal: "Poppins-ExtraLight" },
      300: { normal: "Poppins-Light" },
      400: { normal: "Poppins" },
      500: { normal: "Poppins-Medium" },
      600: { normal: "Poppins-SemiBold" },
      700: { normal: "Poppins-Bold" },
      800: { normal: "Poppins-ExtraBold" },
      900: { normal: "Poppins-Black" },
    },
  },
  body: {
    family: "Poppins",
    size: {
      1: 11,
      2: 12,
      3: 13,
      4: 14,
      5: 16,
      6: 18,
      7: 20,
      8: 22,
      9: 24,
      10: 30,
      11: 36,
      12: 48,
    },
    weight: {
      4: "400",
      5: "500",
      6: "600",
      7: "700",
      8: "800",
      9: "900",
    },
    letterSpacing: {
      4: 0,
      5: -0.25,
      6: -0.5,
      7: -0.75,
      8: -1,
      9: -1.5,
      10: -2,
      11: -2.5,
      12: -3,
    },
    face: {
      100: { normal: "Poppins-Thin" },
      200: { normal: "Poppins-ExtraLight" },
      300: { normal: "Poppins-Light" },
      400: { normal: "Poppins" },
      500: { normal: "Poppins-Medium" },
      600: { normal: "Poppins-SemiBold" },
      700: { normal: "Poppins-Bold" },
      800: { normal: "Poppins-ExtraBold" },
      900: { normal: "Poppins-Black" },
    },
  },
};

// Define Harper brand colors
const colors = {
  ...tokens.color,

  // Primary color - using terracotta as primary
  primary50: "#F8F0ED",
  primary100: "#F2E2DB",
  primary200: "#E9C5B8",
  primary300: "#DEA895",
  primary400: "#D48C71",
  primary500: "#C5705D", // Base color
  primary600: "#B15A4B",
  primary700: "#9D4539",
  primary800: "#7E3127",
  primary900: "#5F1D15",

  // Terracotta (primary) colors
  terracotta50: "#F8F0ED",
  terracotta100: "#F2E2DB",
  terracotta200: "#E9C5B8",
  terracotta300: "#DEA895",
  terracotta400: "#D48C71",
  terracotta500: "#C5705D", // Base color
  terracotta600: "#B15A4B",
  terracotta700: "#9D4539",
  terracotta800: "#7E3127",
  terracotta900: "#5F1D15",

  // Beige colors
  beige50: "#FCFBF9",
  beige100: "#FAF7F3",
  beige200: "#F6F1EA",
  beige300: "#F0E7DA",
  beige400: "#E8DDCD",
  beige500: "#DFD3C3", // Base color
  beige600: "#CCBEA9",
  beige700: "#B9A98F",
  beige800: "#A69575",
  beige900: "#93805B",

  // Brown colors
  brown50: "#F4EEEC",
  brown100: "#E8DDD9",
  brown200: "#D0B8A8", // Base color
  brown300: "#C1A48F",
  brown400: "#B29076",
  brown500: "#A37B5D",
  brown600: "#8C6548",
  brown700: "#754F33",
  brown800: "#5E391E",
  brown900: "#47230A",

  // Cream colors
  cream50: "#FEFCFB",
  cream100: "#FDF9F7",
  cream200: "#FBF5EF",
  cream300: "#F9F1E7",
  cream400: "#F9EDE3",
  cream500: "#F8EDE3", // Base color
  cream600: "#F2DBC3",
  cream700: "#ECCAA3",
  cream800: "#E5B883",
  cream900: "#DFA663",

  // Legacy colors for backward compatibility
  harperTerracotta: "#C5705D",
  harperBeige: "#DFD3C3",
  harperBrown: "#D0B8A8",
  harperCream: "#F8EDE3",
};

// Create the Tamagui configuration
const config = createTamagui({
  themes,
  tokens: {
    ...tokens,
    color: colors,
  },
  shorthands,
  fonts,
});

type Conf = typeof config;
declare module "tamagui" {
  interface TamaguiCustomConfig extends Conf {}
}

export default config;
